// @mui
import { alpha } from '@mui/material/styles'
import { Shadows } from '@mui/material/styles/shadows'
//
import palette from './palette'
import { elevation } from './tokens.generated'

// ----------------------------------------------------------------------
// Four elevation levels and no more.
//
//   e0 flat    1px border, no shadow. The default for every resting surface:
//              cards, panels, table containers, list cards, stat cards. Twenty
//              resting shadows read as corrugation and the eye stops finding
//              the row it wants, and on a dark ground a shadow carries almost
//              no signal on its own — the border does.
//   e1 sticky  Sticky table headers, topbars, sticky form footers. Signals
//              overlap, not float.
//   e2 pop     Menus, popovers, dropdowns, tooltips, toasts, map callouts.
//   e3 modal   Modals, sheets, confirmation dialogs. Always with a scrim.
//
// MUI's 25-step array is folded onto those four, so `elevation={6}` cannot
// invent a fifth. The fold lands where MUI's own defaults already mean:
// AppBar 4 is sticky, Menu and Popover 8 are pop, Drawer 16 and Dialog 24 are
// modal.
// ----------------------------------------------------------------------

interface CustomShadowOptions {
  z1: string
  z8: string
  z12: string
  z16: string
  z20: string
  z24: string
  //
  primary: string
  secondary: string
  info: string
  success: string
  warning: string
  error: string
  //
  card: string
  dialog: string
  dropdown: string
}

declare module '@mui/material/styles' {
  interface Theme {
    customShadows: CustomShadowOptions
  }
  interface ThemeOptions {
    customShadows?: CustomShadowOptions
  }
}

/** MUI elevation index → design-system level. */
const levelForElevation = (index: number): keyof typeof elevation.light => {
  if (index === 0) {
    return 'e0'
  }
  if (index <= 4) {
    return 'e1'
  }
  if (index <= 12) {
    return 'e2'
  }
  return 'e3'
}

const createShadow = (isLight: boolean): Shadows => {
  const levels = isLight ? elevation.light : elevation.dark
  return Array.from(
    { length: 25 },
    (_, index) => levels[levelForElevation(index)],
  ) as unknown as Shadows
}

const createCustomShadow = (isLight: boolean): CustomShadowOptions => {
  const levels = isLight ? elevation.light : elevation.dark
  const colorAlpha = isLight ? 0.18 : 0.34
  return {
    z1: levels.e1,
    z8: levels.e2,
    z12: levels.e2,
    z16: levels.e2,
    z20: levels.e2,
    z24: levels.e3,
    // Coloured shadows are not part of the system. They predate it and are kept
    // for the two screens that use them rather than restyled in this pass.
    primary: `0 8px 16px 0 ${alpha(palette.light.primary.main, colorAlpha)}`,
    secondary: `0 8px 16px 0 ${alpha(
      palette.light.secondary.main,
      colorAlpha,
    )}`,
    info: `0 8px 16px 0 ${alpha(palette.light.info.main, colorAlpha)}`,
    success: `0 8px 16px 0 ${alpha(palette.light.success.main, colorAlpha)}`,
    warning: `0 8px 16px 0 ${alpha(palette.light.warning.main, colorAlpha)}`,
    error: `0 8px 16px 0 ${alpha(palette.light.error.main, colorAlpha)}`,
    //
    card: levels.e0, // flat-first — the Card override applies a 1px border
    dialog: levels.e3,
    dropdown: levels.e2,
  }
}

export const customShadows = {
  light: createCustomShadow(true),
  dark: createCustomShadow(false),
}

const shadows: {
  light: Shadows
  dark: Shadows
} = {
  light: createShadow(true),
  dark: createShadow(false),
}

export default shadows
